import React from 'react';
import MuiBox, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import omit from 'lodash/omit';

import { getPaletteColor } from '@src/utils/colors';
import { getTestId } from '@src/utils/testIds';
import { InternalBoxProps } from './interface';

const BORDER_MAPPING = {
  borderWidth: 'border',
  borderLeftWidth: 'borderLeft',
  borderRightWidth: 'borderRight',
  borderTopWidth: 'borderTop',
  borderBottomWidth: 'borderBottom',
};

export const InternalBox = (props: InternalBoxProps) => {
  return <MuiBox {...medelyPropsToMuiProps(props)} data-testid={getTestId(props)} />;
};

const medelyBorderToMuiBorder = (width: number, style?: string) => {
  // For ui-kit we've decided to split borderStyle and borderWidth into separate props.
  // Howerver they're a single prop in MUI. This will convert the ui-kit border prop to the correct value for MUI.
  if (!!style) {
    return `${width}px ${style}`;
  }
  return width;
};

type SxPropsForMuiBox = {
  borderColor?: string;
  opacity?: number;
  boxShadow?: string;
  boxSizing: 'border-box';
  cursor?: 'pointer';
};

const medelyPropsToMuiProps = ({
  alignContent = 'flex-start', // Default to flex-start to match mobile,
  ...props
}: InternalBoxProps): MuiBoxProps => {
  // Not all props can be passed directly to MUI. In the future we should just manually map all props instead of spread
  const muiDirectProps = omit(props, ['testId', 'data-testid']);
  const muiProps: MuiBoxProps = {
    ...muiDirectProps,
    alignContent,
  };
  const sx: SxPropsForMuiBox = {
    opacity: props.opacity,
    boxSizing: 'border-box',
    cursor: props.onClick ? 'pointer' : undefined,
  };

  const bgColor = props.bgColor ?? props.bgcolor;

  Object.keys(BORDER_MAPPING).forEach((k) => {
    const key = k as keyof typeof BORDER_MAPPING;
    const width = props[key] as number;
    if (!!width) {
      const muiKey = BORDER_MAPPING[key] as keyof MuiBoxProps;
      muiProps[muiKey] = medelyBorderToMuiBorder(width, props.borderStyle);
    }
  });

  if (!!bgColor) {
    muiProps.bgcolor = getPaletteColor(bgColor);
  }

  if (!!props.borderColor) {
    // Set borderColor in sx because it could inconsistently apply depending on the order of the props.
    delete muiProps.borderColor;
    sx.borderColor = getPaletteColor(props.borderColor);
  }

  if (!!props.shadow) {
    sx.boxShadow = props.shadow;
  }

  const { onClick } = props;

  return {
    sx,
    ...muiProps,
    // Our onclick doesn't expect any parameters so we wrap it
    onClick: onClick ? () => onClick() : undefined,
  };
};
